/**
 * <ReviewModal />
 *
 * Phase 17 — buyer leaves (or edits) a seller review on a DELIVERED
 * order. Mounted from the Profile orders card.
 *
 * Backend contract:
 *   POST  /api/orders/{id}/review   (create)
 *   PATCH /api/orders/{id}/review   (edit within REVIEW_EDIT_WINDOW_DAYS)
 *
 * The router does the gating (status === DELIVERED, edit window).
 * This modal trusts the parent to only render it for review-eligible
 * orders and surfaces backend errors verbatim if something slips
 * through.
 */
import * as React from "react";
import { Loader2 } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { StarRating } from "./StarRating";
import type { SellerReview } from "../../lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  /** Pre-filled when editing; null when creating fresh. */
  existing?: SellerReview | null;
  /** Called after a successful save with the fresh review. */
  onSaved: (review: SellerReview) => void;
}

const REVIEW_TEXT_MAX = 800;

export function ReviewModal({
  open,
  onClose,
  orderId,
  existing,
  onSaved,
}: Props) {
  const [rating, setRating] = React.useState<number>(existing?.rating ?? 0);
  const [text, setText] = React.useState<string>(existing?.review_text ?? "");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Reset internal state whenever the modal is reopened on a new order.
  React.useEffect(() => {
    if (open) {
      setRating(existing?.rating ?? 0);
      setText(existing?.review_text ?? "");
      setError(null);
    }
  }, [open, existing]);

  const handleSave = async () => {
    if (rating < 1 || rating > 5) {
      setError("Please choose a rating from 1 to 5 stars.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        rating,
        review_text: text.trim() || null,
      };
      const res = existing
        ? await api.patch<SellerReview>(`/orders/${orderId}/review`, payload)
        : await api.post<SellerReview>(`/orders/${orderId}/review`, payload);
      onSaved(res.data);
      onClose();
    } catch (e) {
      setError(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={existing ? "Edit your review" : "Review this seller"}
      description={
        existing
          ? "You can update your rating or comment within 30 days of posting."
          : "How was your buying experience? Your review helps other buyers."
      }
      testid="review-modal"
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-semibold text-ink mb-1">
            Rating
          </label>
          <StarRating
            value={rating}
            size={20}
            onChange={(n) => setRating(n)}
            ariaLabel="Choose a star rating"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-ink mb-1">
            Comment <span className="text-ink-muted font-normal">(optional)</span>
          </label>
          <textarea
            data-testid="review-textarea"
            className="w-full min-h-[120px] rounded-xl border-2 border-surface-border bg-white p-3 text-ink focus:border-ink focus:outline-none"
            placeholder="Share what worked or what could have been better."
            maxLength={REVIEW_TEXT_MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="text-xs text-ink-muted text-right mt-1">
            {text.length} / {REVIEW_TEXT_MAX}
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="review-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={busy || rating < 1}
            data-testid="review-save"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving…
              </>
            ) : existing ? (
              "Save changes"
            ) : (
              "Submit review"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
